import { v4 as uuidv4 } from "uuid";

import { supabase } from "../../../lib/supabase";

export interface AvailabilitySlot {
  start: string;
  end: string;
}

export interface MentorAvailabilityData {
  uuid: string;
  mentor_uuid: string;
  availability_data: AvailabilitySlot[];
  updated_at: string;
}

export interface SaveResult {
  success: boolean;
  error?: string;
}

export interface GetResult {
  success: boolean;
  data?: MentorAvailabilityData;
  error?: string;
}

/**
 * Saves mentor availability data to the Supabase database.
 * If a record already exists for the mentor, it updates it; otherwise, it creates a new record.
 */
export async function saveMentorAvailability(
  userUuid: string,
  availabilityData: AvailabilitySlot[]
): Promise<SaveResult> {
  try {
    console.log("Saving availability for user:", userUuid);
    console.log("Availability data:", availabilityData);

    // First, get the mentor UUID from the mentor table
    const { data: mentorData, error: mentorError } = await supabase
      .from("mentor")
      .select("uuid")
      .eq("user_uuid", userUuid)
      .single();

    if (mentorError) {
      console.error("Error fetching mentor data:", mentorError);
      return {
        success: false,
        error: "Could not find mentor record for this user",
      };
    }

    if (!mentorData) {
      return { success: false, error: "No mentor record found for this user" };
    }

    const mentorUuid = mentorData.uuid;
    console.log("Found mentor UUID:", mentorUuid);

    // Check if a record already exists for this mentor
    const { data: existingData, error: fetchError } = await supabase
      .from("mentor_availability")
      .select("*")
      .eq("mentor_uuid", mentorUuid)
      .single();

    if (fetchError && fetchError.code !== "PGRST116") {
      // PGRST116 is "no rows returned"
      console.error("Error fetching existing availability:", fetchError);
      return { success: false, error: fetchError.message };
    }

    const availabilityRecord: Partial<MentorAvailabilityData> = {
      uuid: uuidv4(),
      mentor_uuid: mentorUuid,
      availability_data: availabilityData,
    };

    let result;
    if (existingData) {
      // Update existing record
      result = await supabase
        .from("mentor_availability")
        .update(availabilityRecord)
        .eq("mentor_uuid", mentorUuid);
    } else {
      // Create new record
      result = await supabase
        .from("mentor_availability")
        .insert(availabilityRecord);
    }

    if (result.error) {
      console.error("Error saving availability:", result.error);
      return { success: false, error: result.error.message };
    }

    console.log("Successfully saved availability");
    return { success: true };
  } catch (error) {
    console.error("Unexpected error saving availability:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "An unexpected error occurred",
    };
  }
}

/**
 * Retrieves mentor availability data from the Supabase database.
 */
export async function getMentorAvailability(
  mentorUuid: string
): Promise<GetResult> {
  try {
    console.log("Fetching availability for mentor:", mentorUuid);

    const { data, error } = await supabase
      .from("mentor_availability")
      .select("*")
      .eq("mentor_uuid", mentorUuid)
      .single();

    if (error) {
      console.error("Error fetching availability:", error);
      return { success: false, error: error.message };
    }

    if (!data) {
      console.log("No availability data found for mentor:", mentorUuid);
      return { success: true, data: undefined };
    }

    console.log("Successfully fetched availability:", data);
    return { success: true, data: data as MentorAvailabilityData };
  } catch (error) {
    console.error("Unexpected error fetching availability:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "An unexpected error occurred",
    };
  }
}
